import { useCallback, useRef, useState } from 'react';

import { ApiError, tokens } from '@/api/client';
import { auth, type DeletePreviewDTO, type IdentityDTO } from '@/api/endpoints';
import {
  ProviderCancelled, ProviderUnavailable, credentialFor, isAvailable, type Provider,
} from '@/api/providers';
import { useSession } from '@/store/session';
import { BrightnessControl } from '@/components/ui/BrightnessControl';
import { Icon } from '@/components/ui/Icon';
import { ProviderMark } from '@/components/ui/ProviderMark';
import {
  Button, Panel, Row, ScreenBody, ScreenHeader, Stat, StatGrid,
} from '@/components/ui/primitives';

/* Marks come from the shared `ProviderMark`. They used to be strings here — 'G' for Google
   and, for Apple, an EMPTY STRING, so the Apple row shipped with a blank circle. Login.tsx
   had already drawn all three properly; Settings never got the fix because the component
   was private to that file. */
const LINKABLE: { id: Provider; label: string }[] = [
  { id: 'google', label: 'Google' },
  { id: 'apple', label: 'Apple' },
  { id: 'telegram', label: 'Telegram' },
];

export function Settings({ onBack }: { onBack: () => void }) {
  const user = useSession((s) => s.user);
  const flags = useSession((s) => s.flags);
  const signOut = useSession((s) => s.signOut);
  const linkProvider = useSession((s) => s.linkProvider);
  const unlinkProvider = useSession((s) => s.unlinkProvider);
  const [armed, setArmed] = useState(false);

  const [linking, setLinking] = useState<Provider | null>(null);
  const [linkError, setLinkError] = useState<string | null>(null);
  const [linked, setLinked] = useState(false);
  const [unlinking, setUnlinking] = useState<Provider | null>(null);

  /* Account deletion. `stage` walks the player through it rather than putting it behind one
     button: 'closed' → 'open' (what you lose) → 'confirm' (type the word). The preview is
     loaded only when the panel is opened, so an ordinary visit to Settings does not ask the
     server about a wallet nobody is about to destroy. */
  const [stage, setStage] = useState<'closed' | 'open' | 'confirm'>('closed');
  const [preview, setPreview] = useState<DeletePreviewDTO | null>(null);
  const [typed, setTyped] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  /* `undefined` means an older server that does not report identities — which is not the
     same as "none connected". Falling back to `auth_provider` keeps the screen truthful
     about the one identity such a server does know about, rather than inviting the player
     to connect an account they already have. */
  const identities: IdentityDTO[] = user?.identities
    ?? (user && !user.is_guest && user.auth_provider
      ? [{ provider: user.auth_provider as Provider, email: null,
           linked_at: '', last_used_at: null }]
      : []);
  const connected = new Set(identities.map((i) => i.provider));
  const isOnlyWayIn = connected.size <= 1;
  const googleMount = useRef<HTMLDivElement | null>(null);

  const openDelete = useCallback(async () => {
    setDeleteError(null);
    setStage('open');
    try {
      setPreview(await auth.deletePreview());
    } catch {
      /* The panel still opens. A player who wants their account gone must not be stopped by
         a failed *preview* — the server re-checks everything that matters anyway, and it is
         the one that refuses, not this screen. */
      setPreview(null);
    }
  }, []);

  const confirmDelete = useCallback(async () => {
    setDeleting(true);
    setDeleteError(null);
    try {
      await auth.deleteAccount(true);
      /* Gone. Clear the tokens before reloading, or the app comes back holding credentials
         for an account that no longer exists and shows an error instead of the login screen. */
      tokens.clear();
      signOut();
      location.reload();
    } catch (e) {
      setDeleteError(e instanceof ApiError ? e.message : 'Could not delete the account.');
      setDeleting(false);
    }
  }, [signOut]);

  /* Linking keeps the same user row server-side, so coins, depth and collection
     all survive — that is the whole point of offering it after the fact. */
  const link = useCallback(async (provider: Provider) => {
    setLinkError(null);
    setLinking(provider);
    try {
      const credential = await credentialFor(provider, googleMount.current ?? undefined);
      await linkProvider(provider, credential);
      setLinked(true);
    } catch (e) {
      if (e instanceof ProviderCancelled) setLinkError(null);
      else if (e instanceof ProviderUnavailable) setLinkError(e.message);
      else if (e instanceof ApiError) setLinkError(e.message);
      else setLinkError('Could not link that account. Try again.');
    } finally {
      setLinking(null);
    }
  }, [linkProvider]);

  const unlink = useCallback(async (provider: Provider) => {
    setLinkError(null);
    setUnlinking(provider);
    try {
      await unlinkProvider(provider);
    } catch (e) {
      /* The server's refusal — "this is the only way you can sign in" — is the message worth
         showing verbatim; it explains the rule better than anything written here would. */
      setLinkError(e instanceof ApiError ? e.message : 'Could not disconnect that account.');
    } finally {
      setUnlinking(null);
    }
  }, [unlinkProvider]);

  return (
    <ScreenBody>
      <ScreenHeader icon={<Icon name="settings" size={24} />} title="Settings" onBack={onBack} />

      <Panel title="ACCOUNT">
        <StatGrid>
          <Stat k="Name" v={user?.display_name ?? '—'} />
          <Stat k="Type" v={user?.is_guest ? 'Guest' : 'Linked'} />
        </StatGrid>
        {user?.is_guest && (
          <p className="mt-2.5 text-[11px] leading-relaxed text-torch-core">
            You are playing as a guest. Progress is tied to this device and cannot be recovered
            if you lose it — and withdrawals need a linked account.
          </p>
        )}
      </Panel>

      {/*
        Always shown, and every provider gets a row — connected or not.
        This panel used to render only for guests, and only until the first link succeeded,
        which made a second provider unreachable in the UI even once the server could store
        one. A player who connected Telegram could never add Google, and nothing told them
        which account they were actually signed in with.
      */}
      <Panel title="CONNECTED ACCOUNTS" className="mt-3">
        <p className="mb-2.5 text-[11px] leading-relaxed text-ink-dim">
          {user?.is_guest
            ? 'Connect an account so your progress survives this device. Nothing is lost or '
              + 'reset — you keep the same wallet, depth and collection.'
            : 'Any connected account signs you back in to this same player. Connecting more '
              + 'is a second key to the same door, not a second account.'}
        </p>

        <ul className="flex flex-col gap-1.5">
          {LINKABLE.map((p) => {
            const identity = identities.find((i) => i.provider === p.id);
            const busy = linking === p.id || unlinking === p.id;
            return (
              <li
                key={p.id}
                className="flex items-center gap-2.5 rounded-sm border border-ui-lit
                           bg-[linear-gradient(#3A2718,var(--cave-floor))] px-2.5 py-2"
              >
                <span className="grid h-7 w-7 shrink-0 place-items-center rounded-full
                                 bg-black/40 text-ink" aria-hidden>
                  <ProviderMark id={p.id} />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block text-[12px] font-bold text-ink">{p.label}</span>
                  <span className="mt-0.5 block truncate text-[10px] text-ink-dim">
                    {identity
                      ? (identity.email ?? 'Connected')
                      : isAvailable(p.id) ? 'Not connected' : 'Not available on this device'}
                  </span>
                </span>

                {identity ? (
                  <button
                    type="button"
                    onClick={() => void unlink(p.id)}
                    /* The last one cannot go. The server refuses it too — this only saves
                       the player a round trip to be told no. */
                    disabled={busy || isOnlyWayIn}
                    title={isOnlyWayIn
                      ? 'This is the only way you can sign in.'
                      : undefined}
                    className="shrink-0 rounded-sm border border-[rgba(224,85,63,.45)]
                               px-2.5 py-1.5 font-pixel text-[6.5px] tracking-wide
                               text-[#FF9A8B] disabled:opacity-35"
                  >
                    {unlinking === p.id ? '…' : 'DISCONNECT'}
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={() => void link(p.id)}
                    disabled={busy || linking !== null || !isAvailable(p.id)}
                    className="shrink-0 rounded-sm border border-ui-lit bg-black/30
                               px-2.5 py-1.5 font-pixel text-[6.5px] tracking-wide
                               text-torch-core disabled:opacity-35 disabled:grayscale"
                  >
                    {linking === p.id ? '…' : 'CONNECT'}
                  </button>
                )}
              </li>
            );
          })}
        </ul>

        {/* Google Identity Services draws its own button and refuses to render off-screen. */}
        <div ref={googleMount} className="mt-2 flex justify-center empty:hidden" />

        {isOnlyWayIn && identities.length === 1 && (
          <p className="mt-2 text-[10px] leading-relaxed text-ink-dim">
            Connect a second account before disconnecting this one — otherwise there would be
            no way back into this player.
          </p>
        )}
        {linked && (
          <p className="mt-2 text-[11px] leading-relaxed text-[var(--ok)]">
            Connected. Sign in with it on any device to pick up exactly where you are.
          </p>
        )}
        {linkError && (
          <p role="alert" className="mt-2 text-[11px] text-[#FF9A8B]">{linkError}</p>
        )}
      </Panel>

      {/* Sits above FEATURES because it is the only setting on this screen that
          changes what the player sees rather than what the account can do. */}
      <Panel title="DISPLAY" className="mt-3">
        <BrightnessControl />
      </Panel>

      <Panel title="FEATURES" className="mt-3">
        <Row
          icon={<Icon name="ad" />}
          title="Rewarded ads"
          subtitle={flags.ads_enabled ? 'Available' : 'Temporarily disabled'}
          right={
            <span className={`shrink-0 ${flags.ads_enabled ? 'text-[var(--ok)]' : 'text-ink-dim'}`}>
              <Icon name={flags.ads_enabled ? 'check' : 'pause'} size={16} />
            </span>
          }
        />
        <Row
          icon={<Icon name="kyat" />}
          title="Withdrawals"
          subtitle={
            flags.withdraw_enabled
              ? `Minimum ${flags.min_withdraw} Shwe Kyat`
              : 'Link an account and verify your phone first'
          }
          right={
            <span className={`shrink-0 ${flags.withdraw_enabled ? 'text-[var(--ok)]' : 'text-ink-dim'}`}>
              <Icon name={flags.withdraw_enabled ? 'check' : 'lock'} size={16} />
            </span>
          }
        />
      </Panel>

      <Panel title="ABOUT" className="mt-3">
        <p className="text-[11px] leading-relaxed text-ink-dim">
          <b className="text-torch-core">ShweTap</b> — Premium Endless Cave Mining Adventure<br />
          Developed by ReiwaSakura.Tech<br /><br />
          Progress lives on the server, so it follows you across devices once you link an
          account. Nobody has ever reached the bottom.
        </p>
      </Panel>

      <Panel title="SESSION" className="mt-3">
        <p className="mb-2.5 text-[11px] leading-relaxed text-ink-dim">
          Signing out clears the tokens on this device. A guest account is recovered
          automatically from the same device; a linked account needs its provider.
        </p>
        <Button
          variant="danger"
          className="w-full"
          onClick={() => {
            /* Two taps: signing out of a guest account on a lost device is
               unrecoverable, so a single mis-tap must not do it. */
            if (!armed) { setArmed(true); return; }
            tokens.clear();
            signOut();
            location.reload();
          }}
        >
          {armed ? 'Tap again to confirm sign out' : 'Sign out'}
        </Button>
      </Panel>

      {/*
        Deleting the account — a different thing from signing out, and it says so.

        Required by Google Play for any app that creates accounts, and this game creates one
        on the first launch. It is also the *web* half of that requirement: the game runs at
        game.shwetap.com, so this screen is reachable in a browser without installing
        anything (playstore/05_DECLARATIONS.md §5).

        Three steps, not one button. The panel is collapsed until asked for, then it names
        exactly what is lost, then it asks the player to type the word. Each step exists
        because the action is irreversible and there is real money in the account.
      */}
      <Panel title="DELETE ACCOUNT" className="mt-3">
        {stage === 'closed' ? (
          <>
            <p className="mb-2.5 text-[11px] leading-relaxed text-ink-dim">
              Permanently delete this player and everything in it. This is not signing out —
              there is no way back in afterwards, on any device.
            </p>
            <button
              type="button"
              onClick={() => void openDelete()}
              className="w-full rounded-sm border border-[rgba(224,85,63,.45)] px-2.5 py-2
                         font-pixel text-[7px] tracking-wide text-[#FF9A8B]"
            >
              DELETE MY ACCOUNT
            </button>
          </>
        ) : (
          <>
            <p className="mb-2.5 text-[11px] leading-relaxed text-ink">
              This deletes your depth, collection, gear and wallet, and disconnects every
              account you have linked. It cannot be undone.
            </p>

            {preview && (
              <div className="mb-2.5">
                <StatGrid>
                  <Stat k="Shwe Coins lost" v={preview.coins.toLocaleString()} tone="coin" />
                  <Stat k="Shwe Kyat lost" v={preview.kyat} tone="kyat" />
                </StatGrid>
              </div>
            )}

            {/* The one refusal the player can act on. A withdrawal in flight is money on its
                way to a named account — deleting the payee halfway leaves a payment nobody
                can resolve, so the server refuses until it settles or is cancelled. */}
            {preview?.blocked ? (
              <p role="alert" className="mb-2.5 text-[11px] leading-relaxed text-[#FF9A8B]">
                You have a withdrawal in progress. Wait for it to finish, or cancel it, then
                come back — your account cannot be deleted while money is on its way to you.
              </p>
            ) : (
              <>
                {preview && Number(preview.kyat) > 0 && (
                  <p className="mb-2.5 text-[11px] leading-relaxed text-[#FFD08A]">
                    <b>{preview.kyat} Shwe Kyat</b> is real money you have earned. Deleting
                    gives it up. Withdraw it first if you want it.
                  </p>
                )}

                <p className="mb-1.5 text-[10px] text-ink-dim">
                  Type <b className="text-ink">DELETE</b> to confirm.
                </p>
                <input
                  value={typed}
                  onChange={(e) => { setTyped(e.target.value); setStage('confirm'); }}
                  aria-label="Type DELETE to confirm account deletion"
                  autoComplete="off"
                  autoCapitalize="characters"
                  className="mb-2.5 w-full rounded-sm border border-ui-lit bg-black/40 px-2.5
                             py-2 text-[13px] tracking-widest text-ink outline-none
                             focus:border-[rgba(224,85,63,.6)]"
                />
                <Button
                  variant="danger"
                  className="w-full"
                  disabled={typed.trim().toUpperCase() !== 'DELETE' || deleting}
                  onClick={() => void confirmDelete()}
                >
                  {deleting ? 'Deleting…' : 'Delete my account permanently'}
                </Button>
              </>
            )}

            {deleteError && (
              <p role="alert" className="mt-2 text-[11px] text-[#FF9A8B]">{deleteError}</p>
            )}

            <button
              type="button"
              onClick={() => { setStage('closed'); setTyped(''); setDeleteError(null); }}
              className="mt-2 w-full py-1.5 text-[11px] text-ink-dim underline"
            >
              Keep my account
            </button>
          </>
        )}
      </Panel>
    </ScreenBody>
  );
}
